import {
  ChatBubbleLeftRightIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

import { Conversation, RecipientSchema } from '@/services/types/artifact-types';

interface ConversationHeaderProps {
  conversation: Conversation;
  isClientSms?: boolean;
}

export const ConversationHeader = ({
  conversation,
  isClientSms = false,
}: ConversationHeaderProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const firstMessage = conversation.messages?.[0];

  const getConversationType = () => {
    if (isClientSms) return 'Client SMS';
    if (!firstMessage) return 'Conversation';

    switch (firstMessage.message_type) {
      case 'sms':
      case 'mms':
        return 'SMS Thread';
      case 'email':
        return 'Email Thread';
      case 'phone_call':
      case 'call':
        return 'Call Log';
      default:
        return 'Conversation';
    }
  };

  const getConversationIcon = () => {
    if (isClientSms) return <PhoneIcon className="h-4 w-4 text-gray-500" />;
    if (!firstMessage)
      return <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />;

    switch (firstMessage.message_type) {
      case 'sms':
      case 'mms':
        return <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />;
      case 'email':
        return <EnvelopeIcon className="h-4 w-4 text-gray-500" />;
      case 'phone_call':
      case 'call':
        return <PhoneIcon className="h-4 w-4 text-gray-500" />;
      default:
        return <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getParticipants = () => {
    const handles = new Set<string>();

    conversation.messages?.forEach((msg) => {
      if (msg.from_number) handles.add(msg.from_number);
      msg.to_numbers?.forEach((num) => handles.add(num));

      // Include recipient_blob handles if present
      msg.recipient_blob?.recipients.forEach((r: RecipientSchema) => {
        if (r.handle) handles.add(r.handle);
      });
    });

    return Array.from(handles).slice(0, 4); // Show up to 4 participants
  };

  const participants = getParticipants();

  return (
    <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {getConversationIcon()}
            <h2 className="text-lg font-semibold text-gray-900">
              {getConversationType()}
            </h2>
            {isClientSms && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Client
              </span>
            )}
          </div>

          {/* Email Subject Display (if thread is email) */}
          {firstMessage?.message_type === 'email' && firstMessage.subject && (
            <p className="text-sm text-gray-700 mt-1 line-clamp-2">
              {firstMessage.subject}
            </p>
          )}

          {participants.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <UserIcon className="h-3.5 w-3.5" />
              <span>
                {participants.length === 1
                  ? participants[0]
                  : `${participants.length} participants: ${participants.join(', ')}`}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ClockIcon className="h-3.5 w-3.5" />
            <span>Created {formatDate(conversation.created_at)}</span>
            {conversation.modified_at !== conversation.created_at && (
              <span>• Updated {formatDate(conversation.modified_at)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
